import * as React from 'react';
import { useIntl } from 'react-intl';

import { Toaster } from '@notino/react-styleguide';

import { IActions } from '../context/EngravingTryItFirst/model';

import { messages } from './engravingMessages';

const engravingAllowedCharacters =
  /^[\w\sА-ЯҐЄІЇа-яґєіїΑ-Ωα-ωίϊΐόάέύϋΰήώa-zA-Zá-žÁ-Ž?.,!'+|%’–´&#@\-țșȚȘ]+$/i;

interface UseEngravingContentArgs {
  onContentClose: () => void;
  engravingInitials: string;
  changeEngravingInitials: IActions['changeEngravingInitials'];
}

export const useEngravingContent = ({
  onContentClose,
  engravingInitials,
  changeEngravingInitials,
}: UseEngravingContentArgs) => {
  const { formatMessage } = useIntl();
  const [initials, setInitials] = React.useState(engravingInitials);
  const [invalid, setInvalid] = React.useState(false);

  const handleInitialsChange = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const value = target.value.trimStart();
      setInitials(value);

      if (value && !engravingAllowedCharacters.test(value)) {
        setInvalid(true);
      } else {
        setInvalid((old) => old && !value);
      }
    },
    [setInitials, setInvalid]
  );

  const handleSave: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const trimmedInitials = initials.toUpperCase().trim();
      const newIsValid = trimmedInitials.length > 0;

      if (newIsValid && !invalid) {
        changeEngravingInitials(trimmedInitials);
        onContentClose();
        Toaster.info({
          content: formatMessage(messages.addedNotificationText),
          icon: 'IconRegularCheckmark',
        });
      } else {
        setInvalid(true);
      }
    },
    [initials, invalid, changeEngravingInitials, onContentClose, formatMessage]
  );

  return { handleInitialsChange, handleSave, initials, invalid };
};
